import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { KEYWORD_PINS_TYPE } from '../pins/serverTypes';
import { queryKeys } from './queryKeys';

/**
 * Delete a custom keyword pin via
 * `DELETE user/coffee/customizations/keywordPins/{id}` (Real-Magic/Levitate-API#11465).
 * Built-ins are not deletable (the routing layer never produces a delete for
 * one). On success the `keywordPins` query is invalidated.
 */
export function useDeleteKeywordPin(): UseMutationResult<void, Error, string> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<void, Error, string>({
		mutationFn: async id => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<void>(
				ImpersonationBroker.composeApiUrl({ urlPath: `user/coffee/customizations/${KEYWORD_PINS_TYPE}/${id}` }),
				'DELETE'
			);
			if (!result.success) {
				throw asApiError(result);
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.keywordPins() });
		},
		onError: () => {
			toast.error('Failed to delete keyword pin');
		},
	});
}
